// First-login onboarding wizard — mandatory, completed in one sitting.
// Six steps: Personal → Documents → PAN → Aadhar → Bank → Sign-off.
// Nothing is saved server-side until the final submit. Documents are pushed
// first through /api/uploads in sub-MB slices — the reverse proxy refuses a
// body over ~1 MB and all the files together came to far more — and the submit
// itself is a text-only POST to /api/onboarding/complete carrying their ids.
// On success the account's profile_completed flips and the user lands on their
// dashboard.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../api/api";
import { uploadFileInChunks, cancelChunkedUpload } from "../../api/chunkedUpload";
import PdfViewerModal from "../SignOff/PdfViewerModal";
import BackgroundStage from "../../features/backgrounds/BackgroundStage";
import { LogOut } from "lucide-react";
import { canReadDocument, extractCardDetails } from "../../utils/idCardOcr";
import { isValidPhone, sanitizePhoneInput } from "../../utils/phone";
import {
  accountNumberMaxLength,
  sanitizeAccountNumberInput,
  validateAccountNumber,
} from "../../utils/accountNumber";
import { todayStr } from "../../utils/localDate";
import { logout } from "../../api/auth";
import { ConfirmDialog } from "../../components/em/ui";
import "../SignOff/signoff.css";
import "./onboarding.css";

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const STEPS = [
  { key: "personal", label: "Personal" },
  { key: "documents", label: "Documents" },
  { key: "pan", label: "PAN" },
  { key: "aadhar", label: "Aadhar" },
  { key: "bank", label: "Bank" },
  { key: "signoff", label: "Sign-off" },
];

// Per-file ceiling, matching the server's multer limit.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_LABEL = "10 MB";
const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const DEFAULT_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const FORMAT_NAMES = {
  pdf: "PDF",
  doc: "DOC",
  docx: "DOCX",
  jpg: "JPG",
  jpeg: "JPG",
  png: "PNG",
  webp: "WEBP",
};

// "PDF, DOC, DOCX, JPG or PNG" — spelled out from the field's own accept list,
// so what we promise can never drift from what the picker actually takes.
const describeFormats = (accept) => {
  const names = [
    ...new Set(
      String(accept)
        .split(",")
        .map((part) => part.trim().replace(/^\./, "").toLowerCase())
        .filter(Boolean)
        .map((ext) => FORMAT_NAMES[ext] || ext.toUpperCase()),
    ),
  ];
  if (names.length < 2) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
};

// Which step owns each field, and what it is called on screen. Every step
// validates before it lets you past, so this is only for the submit-time
// backstop: it sends you back to the step that can fix the problem instead of
// stranding you on Sign-off. Optional documents are deliberately absent.
const FIELD_INFO = {
  profile_photo: ["Profile photo", 0],
  first_name: ["First name", 0],
  last_name: ["Last name", 0],
  contact_number: ["Contact number", 0],
  dob: ["Date of birth", 0],
  gender: ["Gender", 0],
  address: ["Address", 0],
  blood_group: ["Blood group", 0],
  emergency_contact_name: ["Emergency contact name", 0],
  emergency_contact_relation: ["Relation", 0],
  emergency_contact_phone: ["Emergency contact number", 0],
  offer_letter: ["Offer letter", 1],
  appointment_letter: ["Appointment letter", 1],
  pan_card: ["PAN card", 2],
  pan_name: ["Name as per PAN", 2],
  pan_card_number: ["PAN number", 2],
  aadhar_card: ["Aadhar card", 3],
  aadhar_name: ["Name as per Aadhar", 3],
  aadhar_card_number: ["Aadhar number", 3],
  bank_name: ["Bank name", 4],
  bank_full_name: ["Account holder name", 4],
  bank_account_number: ["Account number", 4],
  bank_branch: ["Branch", 4],
  bank_ifsc: ["IFSC code", 4],
};

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const CATEGORY_LABELS = {
  onboarding: "Onboarding",
  hr_manual: "HR Manual",
  other: "Other information",
};

/* Reusable controls ------------------------------------------------------ */

const Field = ({ label, error, full, children }) => (
  <div
    className={`ob-field${error ? " has-error" : ""}${full ? " ob-field--full" : ""}`}
  >
    <label>
      {label}
      <span className="req">*</span>
    </label>
    {children}
    {error && <span className="ob-field-error">{error}</span>}
  </div>
);

const FileField = ({ label, file, error, accept, onPick, full, optional }) => {
  const ref = useRef(null);
  // Whatever the picked file was rejected for — wrong type or too big.
  const [rejected, setRejected] = useState(null);
  const acceptList = accept || DEFAULT_ACCEPT;
  const allowedExts = acceptList
    .split(",")
    .map((part) => part.trim().replace(/^\./, "").toLowerCase())
    .filter(Boolean);
  // Clearing the input's value matters: without it, picking the same file
  // again after a remove fires no change event.
  const reset = () => {
    if (ref.current) ref.current.value = "";
  };
  const remove = (e) => {
    e.stopPropagation();
    reset();
    setRejected(null);
    onPick(null);
  };
  // The type and size are checked here rather than at submit, so the employee
  // finds out on the step where they can fix it. `accept` on the input only
  // filters the picker's default view — "All files" and drag-and-drop walk
  // straight past it, which is how a spreadsheet reached a photo field.
  const pick = (picked) => {
    if (picked) {
      const ext = (picked.name.split(".").pop() || "").toLowerCase();
      if (!allowedExts.includes(ext)) {
        setRejected(
          `${picked.name} isn't a supported file — upload ${describeFormats(acceptList)}.`,
        );
        reset();
        onPick(null);
        return;
      }
      if (picked.size > MAX_FILE_BYTES) {
        setRejected(
          `${picked.name} is ${formatSize(picked.size)} — the limit is ${MAX_FILE_LABEL}. Please upload a smaller file.`,
        );
        reset();
        onPick(null);
        return;
      }
    }
    setRejected(null);
    onPick(picked);
  };
  const shownError = rejected || error;
  return (
    <div className={`ob-field${full ? " ob-field--full" : ""}`}>
      <div
        className={`ob-file${file ? " has-file" : ""}${shownError ? " has-error" : ""}`}
        onClick={() => ref.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && ref.current?.click()}
      >
        <span className="ob-file-icon" aria-hidden="true">
          {file ? "✅" : "📄"}
        </span>
        <span className="ob-file-meta">
          <span className="ob-file-label">
            {label}
            {/* Required fields carry the asterisk; optional ones simply don't. */}
            {!optional && (
              <span className="req" style={{ color: "#f54b01" }}>
                *
              </span>
            )}
          </span>
          <span className="ob-file-hint">
            {file ? (
              <>
                <span className="ob-file-name">{file.name}</span>
                <span className="ob-file-size">{formatSize(file.size)}</span>
              </>
            ) : (
              "Click to upload"
            )}
          </span>
          {/* Formats and the size cap stay on their own line so neither is
              ever the thing that gets truncated. */}
          <span className="ob-file-specs">
            {describeFormats(acceptList)} · Max {MAX_FILE_LABEL}
          </span>
        </span>
        {file && (
          <button
            type="button"
            className="ob-file-clear"
            onClick={remove}
            onKeyDown={(e) => e.stopPropagation()}
            title={`Remove ${label.toLowerCase()}`}
            aria-label={`Remove ${label.toLowerCase()}`}
          >
            ✕
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept={acceptList}
          onChange={(e) => pick(e.target.files?.[0] || null)}
        />
      </div>
      {shownError && <span className="ob-field-error">{shownError}</span>}
    </div>
  );
};

// Progress/result line for a card being auto-read. Module scope on purpose:
// nested inside the wizard it would remount on every progress tick and restart
// the spinner animation.
const ScanStatus = ({ scan }) => {
  if (!scan || scan.status === "idle") return null;
  return (
    <div className={`ob-scan ob-scan--${scan.status}`} aria-live="polite">
      {scan.status === "scanning" ? (
        <>
          <span className="ob-scan-spin" aria-hidden="true" />
          <span>Reading your card… {Math.round(scan.progress * 100)}%</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">{scan.status === "ok" ? "✨" : "⚠️"}</span>
          <span>{scan.note}</span>
        </>
      )}
    </div>
  );
};

/* The wizard ------------------------------------------------------------- */

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Documents go up one at a time before the submit, so the button has to say
  // more than "Submitting…" — a wizard with 17 MB of files takes a while.
  const [uploadNote, setUploadNote] = useState(null);
  const [done, setDone] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Text fields — keyed exactly as the API expects.
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    contact_number: "",
    dob: "",
    gender: "",
    address: "",
    blood_group: "",
    emergency_contact_name: "",
    emergency_contact_relation: "",
    emergency_contact_phone: "",
    pan_name: "",
    pan_card_number: "",
    aadhar_name: "",
    aadhar_card_number: "",
    bank_name: "",
    bank_full_name: "",
    bank_account_number: "",
    bank_branch: "",
    bank_ifsc: "",
  });

  // File fields — keyed exactly as the API expects.
  const [files, setFiles] = useState({
    profile_photo: null,
    resume: null,
    edu_certificates: null,
    offer_letter: null,
    confirmation_letter: null,
    appointment_letter: null,
    pan_card: null,
    aadhar_card: null,
  });

  // Sign-off documents + which the user has ticked.
  const [docs, setDocs] = useState([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [acked, setAcked] = useState(new Set());
  // View-only reader: docs must be opened before they can be ticked.
  const [readerDoc, setReaderDoc] = useState(null);
  const [viewedIds, setViewedIds] = useState(() => new Set());

  // Who is filling this in — read once from the session the login stored.
  const account = useMemo(() => {
    let name = "";
    try {
      const u = JSON.parse(localStorage.getItem("userdata") || "{}");
      name =
        [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
        u.username ||
        u.email ||
        "";
    } catch {
      /* no session details to show — the chip still logs out */
    }
    return {
      name: name || "Your account",
      initial: (name || "?").trim().charAt(0).toUpperCase(),
    };
  }, []);

  const photoPreview = useMemo(
    () =>
      files.profile_photo ? URL.createObjectURL(files.profile_photo) : null,
    [files.profile_photo],
  );
  useEffect(
    () => () => photoPreview && URL.revokeObjectURL(photoPreview),
    [photoPreview],
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/signoff/documents`, auth());
        setDocs(res.data || []);
      } catch (e) {
        console.warn("[onboarding] signoff docs load failed", e?.message);
      } finally {
        setDocsLoaded(true);
      }
    })();
  }, []);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };
  const pickFile = (key) => (file) => {
    setFiles((f) => ({ ...f, [key]: file }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  /* ID card auto-read -----------------------------------------------------
     Uploading a PAN or Aadhar card runs OCR in the browser and fills the two
     fields below it. Values the user typed themselves are never overwritten,
     and a failed read is silent-but-visible: the fields stay editable. */
  const [scans, setScans] = useState({});
  const scanRunId = useRef({});
  const autofilled = useRef({});
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const setScan = (fileKey, state) =>
    setScans((s) => ({ ...s, [fileKey]: state }));

  const readCard = (fileKey, cfg) => async (file) => {
    pickFile(fileKey)(file);
    const runId = (scanRunId.current[fileKey] || 0) + 1;
    scanRunId.current[fileKey] = runId;
    const isCurrent = () => scanRunId.current[fileKey] === runId;

    if (!file) {
      // Removing the card takes back what the scan filled in, but leaves
      // anything the user typed over it alone.
      const current = formRef.current;
      const patch = {};
      [cfg.nameKey, cfg.numberKey].forEach((key) => {
        if (autofilled.current[key] && current[key] === autofilled.current[key]) {
          patch[key] = "";
          autofilled.current[key] = "";
        }
      });
      if (Object.keys(patch).length) setForm((f) => ({ ...f, ...patch }));
      setScan(fileKey, { status: "idle", progress: 0, note: "" });
      return;
    }
    if (!canReadDocument(file)) {
      setScan(fileKey, { status: "idle", progress: 0, note: "" });
      return;
    }

    setScan(fileKey, { status: "scanning", progress: 0, note: "" });
    try {
      const { name, number } = await extractCardDetails(file, cfg.kind, {
        onProgress: (p) => {
          if (!isCurrent()) return;
          setScans((s) =>
            s[fileKey]?.status === "scanning"
              ? { ...s, [fileKey]: { ...s[fileKey], progress: p } }
              : s,
          );
        },
      });
      if (!isCurrent()) return; // a newer upload superseded this one

      const current = formRef.current;
      const patch = {};
      const filled = [];
      const apply = (key, value, label) => {
        if (!value) return;
        const typedByUser =
          String(current[key] || "").trim() &&
          current[key] !== autofilled.current[key];
        if (typedByUser) return;
        patch[key] = value;
        autofilled.current[key] = value;
        filled.push(label);
      };
      apply(cfg.nameKey, name, "the name");
      apply(cfg.numberKey, number, cfg.numberLabel);

      if (filled.length) {
        setForm((f) => ({ ...f, ...patch }));
        setErrors((er) => ({
          ...er,
          [cfg.nameKey]: undefined,
          [cfg.numberKey]: undefined,
        }));
        setScan(fileKey, {
          status: "ok",
          progress: 1,
          note: `Read ${filled.join(" and ")} off your card — please check it matches the print exactly.`,
        });
      } else if (name || number) {
        setScan(fileKey, {
          status: "ok",
          progress: 1,
          note: "Kept the details you typed — the card was read but nothing was overwritten.",
        });
      } else {
        setScan(fileKey, {
          status: "warn",
          progress: 1,
          note: "Couldn't read this card automatically — please type the details below.",
        });
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.warn(`[onboarding] ${cfg.kind} read failed`, e?.message);
      setScan(fileKey, {
        status: "warn",
        progress: 1,
        note: "Couldn't read this card automatically — please type the details below.",
      });
    }
  };

  const readPanCard = readCard("pan_card", {
    kind: "pan",
    nameKey: "pan_name",
    numberKey: "pan_card_number",
    numberLabel: "the PAN number",
  });
  const readAadharCard = readCard("aadhar_card", {
    kind: "aadhaar",
    nameKey: "aadhar_name",
    numberKey: "aadhar_card_number",
    numberLabel: "the Aadhar number",
  });

  /* Per-step validation — mandatory unless a field is marked optional. */
  const validateStep = (idx) => {
    const er = {};
    const need = (key, label) => {
      if (!String(form[key] || "").trim()) er[key] = `${label} is required`;
    };
    const needFile = (key, label) => {
      if (!files[key]) er[key] = `${label} is required`;
    };
    const needPhone = (key, label) => {
      const value = String(form[key] || "").trim();
      if (!value) er[key] = `${label} is required`;
      else if (!isValidPhone(value))
        er[key] = "Enter a valid 10-digit mobile number, e.g. 98765 43210";
    };
    // Required, and checked on the value with spaces/hyphens already stripped
    // — the same rule the server applies on submit.
    const needAccountNumber = (key, label) => {
      const { ok, error } = validateAccountNumber(form[key], { label });
      if (!ok) er[key] = error;
    };

    if (idx === 0) {
      needFile("profile_photo", "Profile photo");
      need("first_name", "First name");
      need("last_name", "Last name");
      needPhone("contact_number", "Contact number");
      need("dob", "Date of birth");
      // `max` on the input only constrains the picker — a typed or pasted date
      // sails past it, so the value is checked here too.
      if (form.dob && form.dob > todayStr())
        er.dob = "Date of birth can't be in the future";
      need("gender", "Gender");
      need("address", "Address");
      need("blood_group", "Blood group");
      need("emergency_contact_name", "Emergency contact name");
      need("emergency_contact_relation", "Relation");
      needPhone("emergency_contact_phone", "Emergency contact number");
    } else if (idx === 1) {
      // CV, education certificates and the confirmation letter are optional.
      needFile("offer_letter", "Offer letter");
      needFile("appointment_letter", "Appointment letter");
    } else if (idx === 2) {
      needFile("pan_card", "PAN card");
      need("pan_name", "Name as per PAN");
      need("pan_card_number", "PAN number");
    } else if (idx === 3) {
      needFile("aadhar_card", "Aadhar card");
      need("aadhar_name", "Name as per Aadhar");
      need("aadhar_card_number", "Aadhar number");
    } else if (idx === 4) {
      need("bank_name", "Bank name");
      need("bank_full_name", "Account holder name");
      needAccountNumber("bank_account_number", "Account number");
      need("bank_branch", "Branch");
      need("bank_ifsc", "IFSC code");
    } else if (idx === 5) {
      const unticked = docs.filter((d) => !acked.has(d.id));
      if (unticked.length)
        er._signoff = "Please read and tick every document to continue.";
    }

    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validateStep(5)) return;

    // ── Pre-flight: backstop for the per-field size check ────────────────
    const oversized = Object.values(files)
      .filter((f) => f && f.size > MAX_FILE_BYTES)
      .map((f) => `${f.name} (${formatSize(f.size)})`);
    if (oversized.length) {
      setSubmitError(
        `The following file(s) exceed the ${MAX_FILE_LABEL} limit — please use smaller files:\n• ${oversized.join("\n• ")}`,
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // ─────────────────────────────────────────────────────────────────────

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Every document in one POST is ~17 MB of body, and the reverse proxy in
      // front of the API refuses anything over ~1 MB — which is what answered
      // this submit with a 413 and the misleading "reduce the file sizes",
      // while each file sat well under the 10 MB cap. Send the documents ahead
      // in slices instead (see api/chunkedUpload) and submit only their ids,
      // so the POST below carries nothing but text.
      const picked = Object.entries(files).filter(([, f]) => f);
      const uploads = {};
      try {
        for (let i = 0; i < picked.length; i += 1) {
          const [field, file] = picked[i];
          setUploadNote(
            `Uploading ${FIELD_INFO[field]?.[0] || field} (${i + 1} of ${picked.length})…`,
          );
          uploads[field] = await uploadFileInChunks(file);
        }
        setUploadNote("Finishing up…");

        const fd = new FormData();
        // Text fields stay on a FormData so every value reaches the server as
        // the same string it always has — a JSON body would start sending
        // numbers and booleans to a controller that only ever read strings.
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("uploads", JSON.stringify(uploads));
        fd.append("acknowledged_doc_ids", JSON.stringify([...acked]));

        await axios.post(`${API_URL}/api/onboarding/complete`, fd, auth());
      } catch (err) {
        // Nothing claimed the documents already up there, whether the failure
        // was mid-upload or at the submit. Release them rather than leaving
        // part files to age out — and to keep the per-user session cap free
        // for the retry the user is about to make.
        Object.values(uploads).forEach(cancelChunkedUpload);
        throw err;
      }

      // Flip the local gate so routing lets the user through.
      try {
        const u = JSON.parse(localStorage.getItem("userdata") || "{}");
        u.profile_completed = 1;
        if (form.first_name) u.first_name = form.first_name;
        if (form.last_name) u.last_name = form.last_name;
        localStorage.setItem("userdata", JSON.stringify(u));
      } catch { }

      setDone(true);
      const role = (localStorage.getItem("role") || "member").toLowerCase();
      const basePath =
        role === "hr" ? "/hr" : role === "admin" ? "/admin" : "/member";
      setTimeout(
        () => navigate(`${basePath}/dashboard`, { replace: true }),
        1600,
      );
    } catch (err) {
      const status = err?.response?.status;
      // Controllers answer with a plain string; the central error handler
      // wraps its message in an envelope. Never hand an object to React.
      const raw = err?.response?.data?.error;
      const serverMsg = typeof raw === "string" ? raw : raw?.message;

      // The server named the fields it rejected — take the user to the step
      // that owns the first of them, with the fields flagged there.
      const rejected = (err?.response?.data?.fields || []).filter(
        (f) => FIELD_INFO[f],
      );
      if (rejected.length) {
        setErrors(
          Object.fromEntries(
            rejected.map((f) => [f, `${FIELD_INFO[f][0]} is required`]),
          ),
        );
        setStep(Math.min(...rejected.map((f) => FIELD_INFO[f][1])));
        setSubmitError(
          `Please add ${rejected
            .map((f) => FIELD_INFO[f][0].toLowerCase())
            .join(", ")} — we've taken you back to that step.`,
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      // Documents now travel in sub-MB slices, so a 413 no longer means the
      // combined payload was too big — it means the server's request limit is
      // lower still. Telling the user to shrink files they already sized
      // correctly is what sent people round in circles.
      const friendlyMsg =
        status === 413
          ? "The server refused the upload — its request size limit is set lower than this app can work with. Please contact your administrator."
          : serverMsg ||
          "Could not complete onboarding. Please check your details and try again.";
      setSubmitError(friendlyMsg);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
      setUploadNote(null);
    }
  };

  const toggleAck = (id) => {
    setAcked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setErrors((er) => ({ ...er, _signoff: undefined }));
  };

  /* Step renderers -------------------------------------------------------- */

  const renderPersonal = () => (
    <>
      <h2 className="ob-card-title">Personal details</h2>
      <p className="ob-card-sub">
        Tell us about yourself — this builds your employee profile.
      </p>

      <div className="ob-avatar-row">
        <div className="ob-avatar">
          {photoPreview ? (
            <img src={photoPreview} alt="Profile preview" />
          ) : (
            <span aria-hidden="true">🙂</span>
          )}
        </div>
        <FileField
          label="Profile photo"
          file={files.profile_photo}
          error={errors.profile_photo}
          accept=".jpg,.jpeg,.png"
          onPick={pickFile("profile_photo")}
          full
        />
      </div>

      <div className="ob-grid">
        <Field label="First name" error={errors.first_name}>
          <input
            value={form.first_name}
            onChange={set("first_name")}
            placeholder="e.g. Priya"
          />
        </Field>
        <Field label="Last name" error={errors.last_name}>
          <input
            value={form.last_name}
            onChange={set("last_name")}
            placeholder="e.g. Sharma"
          />
        </Field>
        <Field label="Contact number" error={errors.contact_number}>
          <input
            type="tel"
            inputMode="tel"
            value={form.contact_number}
            onChange={(e) =>
              set("contact_number")(sanitizePhoneInput(e.target.value))
            }
            placeholder="+91 98765 43210"
          />
        </Field>
        <Field label="Date of birth" error={errors.dob}>
          <input
            type="date"
            value={form.dob}
            onChange={set("dob")}
            max={todayStr()}
          />
        </Field>
        <Field label="Gender" error={errors.gender}>
          <select value={form.gender} onChange={set("gender")}>
            <option value="">Select…</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Blood group" error={errors.blood_group}>
          <select value={form.blood_group} onChange={set("blood_group")}>
            <option value="">Select…</option>
            {BLOOD_GROUPS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Address" error={errors.address} full>
          <textarea
            value={form.address}
            onChange={set("address")}
            placeholder="House / street / city / state / PIN"
          />
        </Field>

        <div className="ob-section">Emergency contact</div>
        <Field label="Contact name" error={errors.emergency_contact_name}>
          <input
            value={form.emergency_contact_name}
            onChange={set("emergency_contact_name")}
            placeholder="e.g. Rohan Sharma"
          />
        </Field>
        <Field label="Relation" error={errors.emergency_contact_relation}>
          <input
            value={form.emergency_contact_relation}
            onChange={set("emergency_contact_relation")}
            placeholder="e.g. Spouse"
          />
        </Field>
        <Field label="Contact number" error={errors.emergency_contact_phone}>
          <input
            type="tel"
            inputMode="tel"
            value={form.emergency_contact_phone}
            onChange={(e) =>
              set("emergency_contact_phone")(sanitizePhoneInput(e.target.value))
            }
            placeholder="+91 91234 56789"
          />
        </Field>
      </div>
    </>
  );

  const renderDocuments = () => (
    <>
      <h2 className="ob-card-title">Documents</h2>
      <p className="ob-card-sub">
        Upload your employment paperwork. The offer letter and appointment
        letter are required — the rest you can add later.
      </p>
      <div className="ob-grid">
        <FileField
          label="CV / Resume"
          file={files.resume}
          error={errors.resume}
          onPick={pickFile("resume")}
          optional
          full
        />
        <FileField
          label="Education certificates"
          file={files.edu_certificates}
          error={errors.edu_certificates}
          onPick={pickFile("edu_certificates")}
          optional
          full
        />
        <FileField
          label="Offer letter"
          file={files.offer_letter}
          error={errors.offer_letter}
          onPick={pickFile("offer_letter")}
          full
        />
        <FileField
          label="Confirmation letter"
          file={files.confirmation_letter}
          error={errors.confirmation_letter}
          onPick={pickFile("confirmation_letter")}
          optional
          full
        />
        <FileField
          label="Appointment letter"
          file={files.appointment_letter}
          error={errors.appointment_letter}
          onPick={pickFile("appointment_letter")}
          full
        />
      </div>
    </>
  );

  const renderPan = () => (
    <>
      <h2 className="ob-card-title">PAN card</h2>
      <p className="ob-card-sub">
        Upload your PAN — we'll read the name and number off it — then confirm
        the details match exactly as printed.
      </p>
      <div className="ob-grid">
        <FileField
          label="PAN card"
          file={files.pan_card}
          error={errors.pan_card}
          accept=".pdf,.jpg,.jpeg,.png"
          onPick={readPanCard}
          full
        />
        <ScanStatus scan={scans.pan_card} />
        <Field label="Name as per PAN" error={errors.pan_name}>
          <input
            value={form.pan_name}
            onChange={set("pan_name")}
            placeholder="Full name on the card"
          />
        </Field>
        <Field label="PAN number" error={errors.pan_card_number}>
          <input
            value={form.pan_card_number}
            onChange={(e) =>
              set("pan_card_number")(e.target.value.toUpperCase())
            }
            placeholder="ABCDE1234F"
            maxLength={10}
          />
        </Field>
      </div>
    </>
  );

  const renderAadhar = () => (
    <>
      <h2 className="ob-card-title">Aadhar card</h2>
      <p className="ob-card-sub">
        Upload your Aadhar — we'll read the name and number off it — then
        confirm the details match exactly as printed.
      </p>
      <div className="ob-grid">
        <FileField
          label="Aadhar card"
          file={files.aadhar_card}
          error={errors.aadhar_card}
          accept=".pdf,.jpg,.jpeg,.png"
          onPick={readAadharCard}
          full
        />
        <ScanStatus scan={scans.aadhar_card} />
        <Field label="Name as per Aadhar" error={errors.aadhar_name}>
          <input
            value={form.aadhar_name}
            onChange={set("aadhar_name")}
            placeholder="Full name on the card"
          />
        </Field>
        <Field label="Aadhar number" error={errors.aadhar_card_number}>
          <input
            value={form.aadhar_card_number}
            onChange={(e) =>
              set("aadhar_card_number")(e.target.value.replace(/[^\d ]/g, ""))
            }
            placeholder="1234 5678 9012"
            maxLength={14}
          />
        </Field>
      </div>
    </>
  );

  const renderBank = () => (
    <>
      <h2 className="ob-card-title">Bank details</h2>
      <p className="ob-card-sub">
        Where your salary should land. Double-check the account number.
      </p>
      <div className="ob-grid">
        <Field label="Bank name" error={errors.bank_name}>
          <input
            value={form.bank_name}
            onChange={set("bank_name")}
            placeholder="e.g. HDFC Bank"
          />
        </Field>
        <Field label="Account holder name" error={errors.bank_full_name}>
          <input
            value={form.bank_full_name}
            onChange={set("bank_full_name")}
            placeholder="As per the bank account"
          />
        </Field>
        <Field label="Account number" error={errors.bank_account_number}>
          <input
            value={form.bank_account_number}
            // Digits only, separators stripped, hard-capped at the stored
            // length — so a pasted "1234 5678 9012" lands clean and typing
            // simply stops at the cap.
            onChange={(e) =>
              set("bank_account_number")(
                sanitizeAccountNumberInput(e.target.value),
              )
            }
            onBlur={(e) =>
              set("bank_account_number")(
                sanitizeAccountNumberInput(e.target.value),
              )
            }
            inputMode="numeric"
            autoComplete="off"
            maxLength={accountNumberMaxLength()}
            placeholder="Account number"
          />
        </Field>
        <Field label="Branch" error={errors.bank_branch}>
          <input
            value={form.bank_branch}
            onChange={set("bank_branch")}
            placeholder="e.g. Koramangala"
          />
        </Field>
        <Field label="IFSC code" error={errors.bank_ifsc}>
          <input
            value={form.bank_ifsc}
            onChange={(e) => set("bank_ifsc")(e.target.value.toUpperCase())}
            placeholder="HDFC0001234"
            maxLength={11}
          />
        </Field>
      </div>
    </>
  );

  const renderSignoff = () => (
    <>
      <h2 className="ob-card-title">Sign-off documents</h2>
      <p className="ob-card-sub">
        Read each document, then tick to confirm you've read and understood it.
        Your sign-off is recorded with a timestamp.
      </p>
      {errors._signoff && (
        <div className="ob-banner-error">{errors._signoff}</div>
      )}
      {!docsLoaded ? (
        <div className="ob-doc-empty">Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="ob-doc-empty">
          No sign-off documents have been published yet — you're all set.
          <br />
          Hit <strong>Finish</strong> to complete your onboarding.
        </div>
      ) : (
        <div className="ob-doc-list">
          {docs.map((d) => {
            const viewed = viewedIds.has(d.id);
            return (
              <div
                key={d.id}
                className={`ob-doc${acked.has(d.id) ? " is-acked" : ""}`}
              >
                <input
                  type="checkbox"
                  className="ob-doc-check"
                  checked={acked.has(d.id)}
                  disabled={!viewed && !acked.has(d.id)}
                  title={
                    viewed || acked.has(d.id)
                      ? undefined
                      : "Open the document first"
                  }
                  onChange={() => toggleAck(d.id)}
                  aria-label={`I have read ${d.title}`}
                />
                <div className="ob-doc-body">
                  <p className="ob-doc-title">{d.title}</p>
                  <span className="ob-doc-cat">
                    {CATEGORY_LABELS[d.category] || d.category}
                  </span>
                  {d.description && (
                    <p className="ob-doc-desc">{d.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="ob-doc-open"
                  onClick={() => setReaderDoc(d)}
                >
                  Read ↗
                </button>
              </div>
            );
          })}
        </div>
      )}
      {readerDoc && (
        <PdfViewerModal
          doc={readerDoc}
          onClose={() => setReaderDoc(null)}
          onViewed={(id) =>
            setViewedIds((prev) => {
              if (prev.has(id)) return prev;
              const next = new Set(prev);
              next.add(id);
              return next;
            })
          }
        />
      )}
    </>
  );

  const renderStep = () =>
    [
      renderPersonal,
      renderDocuments,
      renderPan,
      renderAadhar,
      renderBank,
      renderSignoff,
    ][step]();

  if (done) {
    return (
      <div className="ob-wizard-bg">
        {/* Admin-set default background scene renders behind the wizard
            (fixed, z-index −1, inert). The wizard bg goes transparent via
            body.emhub-bg-active when a scene resolves. */}
        <BackgroundStage />
        <div className="ob-wizard">
          <div className="ob-card ob-done">
            <div className="ob-welcome-icon" aria-hidden="true">
              🎉
            </div>
            <h1 className="ob-welcome-title">You're all set!</h1>
            <p className="ob-welcome-body">
              Your profile has been created. Taking you to your dashboard…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ob-wizard-bg">
      <BackgroundStage />
      <div className="ob-wizard">
        {/* Onboarding is a gate with no navigation of its own, so this chip is
            the only way out for someone who signed in as the wrong person —
            and it names who that is. */}
        <div className="ob-userchip">
          <span className="ob-userchip-avatar" aria-hidden="true">
            {account.initial}
          </span>
          <span className="ob-userchip-meta">
            <span className="ob-userchip-name">{account.name}</span>
          </span>
          <span className="ob-userchip-rule" aria-hidden="true" />
          <button
            type="button"
            className="ob-logout"
            onClick={() => setConfirmLogout(true)}
            disabled={submitting}
          >
            <LogOut size={15} aria-hidden="true" /> Log out
          </button>
        </div>

        <div className="ob-head">
          <h1>Welcome aboard </h1>
          <p>Let's set up your profile — it takes about five minutes.</p>
        </div>

        <div className="ob-steps" role="tablist" aria-label="Onboarding steps">
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className={`ob-step${i === step ? " is-active" : ""}${i < step ? " is-done" : ""}`}
            >
              <span className="ob-step-dot">{i < step ? "✓" : i + 1}</span>
              {s.label}
            </span>
          ))}
        </div>

        <div className="ob-card">
          {submitError && <div className="ob-banner-error">{submitError}</div>}
          {renderStep()}

          <div className="ob-nav">
            {step > 0 ? (
              <button
                type="button"
                className="ob-btn ob-btn--ghost"
                onClick={goBack}
                disabled={submitting}
              >
                ← Back
              </button>
            ) : (
              <span className="ob-nav-note">All fields are required</span>
            )}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                className="ob-btn ob-btn--primary"
                onClick={goNext}
              >
                Continue →
              </button>
            ) : (
              <button
                type="button"
                className="ob-btn ob-btn--primary"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? uploadNote || "Finishing…" : "Finish onboarding 🎉"}
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="Log out?"
        message="Nothing here is saved until you finish, so anything you've filled in will be lost."
        confirmLabel="Log out"
        danger
        onConfirm={logout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
